import React from 'react'
import { useTokens } from '../../lib/api'
import { useT } from '../../lib/i18n'

const LABEL: Record<string, string> = {
  erp_kaytech:        'KayTech ERP',
  kaytechnologie:     'KayTechnologie.com',
  erp_assg:           'ERP ASSG',
  erp_supmanagement:  'ERP Sup Management',
  tontine_app:        'TontineGA',
  erp_agasa:          'ERP AGASA',
  cumcco_ga:          'CUMCCO GA',
}

function fmt(n: number): string {
  if (n === 0) return '—'
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)} M`
  if (n >= 1_000) return `${Math.round(n / 1_000)} k`
  return String(n)
}

function UsageBar({ pct, color }: { pct: number; color: string }) {
  const clamped = Math.min(100, Math.max(0, pct))
  return (
    <div style={{
      height: 6, borderRadius: 3,
      background: 'var(--border)', overflow: 'hidden', flexShrink: 0, width: '100%',
    }}>
      <div style={{
        height: '100%', width: `${clamped}%`,
        background: color, borderRadius: 3, transition: 'width 0.4s ease',
      }} />
    </div>
  )
}

function tokenColor(pct: number): string {
  if (pct >= 90) return 'var(--health-crit)'
  if (pct >= 70) return 'var(--health-warn)'
  return 'var(--health-good)'
}

export default function Tokens() {
  const { data, loading, error } = useTokens()
  const t = useT()

  if (loading) {
    return (
      <div style={{ padding: '48px 24px', textAlign: 'center', color: 'var(--text-muted)', fontSize: 14 }}>
        {t('tok_loading')}
      </div>
    )
  }

  if (error || !data) {
    return (
      <div style={{ padding: '48px 24px', textAlign: 'center', color: 'var(--health-crit)', fontSize: 14 }}>
        {t('tok_error')}
      </div>
    )
  }

  const totalConsumed = data.reduce((s, p) => s + p.consumed, 0)
  const totalEstimated = data.reduce((s, p) => s + p.estimated, 0)
  const totalPct = totalEstimated > 0 ? Math.round((totalConsumed / totalEstimated) * 100) : 0

  return (
    <div style={{ padding: '0 24px 24px', maxWidth: 960, margin: '0 auto' }}>
      {/* ── KPI globaux ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 24 }}>
        {([
          { key: 'tok_consumed',  value: fmt(totalConsumed),  color: 'var(--text)' },
          { key: 'tok_estimated', value: fmt(totalEstimated), color: 'var(--text-muted)' },
          { key: 'tok_usage',     value: `${totalPct}%`,      color: tokenColor(totalPct) },
        ] as const).map((kpi) => (
          <div key={kpi.key} style={{
            background: 'var(--surface)', border: '1px solid var(--border)',
            borderRadius: 'var(--radius-lg)', padding: '16px 20px',
          }}>
            <div style={{ fontSize: 22, fontWeight: 700, color: kpi.color }}>{kpi.value}</div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>{t(kpi.key)}</div>
          </div>
        ))}
      </div>

      {/* ── Table projets ── */}
      <div style={{
        background: 'var(--surface)', border: '1px solid var(--border)',
        borderRadius: 'var(--radius-lg)', overflow: 'hidden',
      }}>
        {/* En-tête */}
        <div style={{
          padding: '14px 20px', borderBottom: '1px solid var(--border)',
          fontWeight: 600, fontSize: 13, color: 'var(--text-muted)',
          display: 'grid', gridTemplateColumns: '2fr 100px 100px 80px 180px', gap: 12,
        }}>
          <span>{t('tok_project')}</span>
          <span style={{ textAlign: 'right' }}>{t('tok_consumed')}</span>
          <span style={{ textAlign: 'right' }}>{t('tok_estimated')}</span>
          <span style={{ textAlign: 'right' }}>{t('tok_pct')}</span>
          <span>{t('tok_token_usage')}</span>
        </div>

        {/* Lignes projets */}
        {data.map((p, i) => {
          const usagePct = p.estimated > 0 ? Math.round((p.consumed / p.estimated) * 100) : 0
          const color = tokenColor(usagePct)
          return (
            <div key={p.projectId} style={{
              padding: '14px 20px',
              borderTop: i === 0 ? 'none' : '1px solid var(--border)',
              display: 'grid', gridTemplateColumns: '2fr 100px 100px 80px 180px',
              gap: 12, alignItems: 'center',
            }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>
                  {LABEL[p.projectId] ?? p.projectId}
                </div>
                {!p.hasBudgetFile && (
                  <div style={{ fontSize: 11, color: 'var(--health-warn)', marginTop: 2 }}>
                    {t('tok_no_budget')}
                  </div>
                )}
              </div>
              <div style={{ textAlign: 'right', fontSize: 13, fontFamily: 'monospace', color: 'var(--text)' }}>
                {fmt(p.consumed)}
              </div>
              <div style={{ textAlign: 'right', fontSize: 13, fontFamily: 'monospace', color: 'var(--text-muted)' }}>
                {fmt(p.estimated)}
              </div>
              <div style={{ textAlign: 'right', fontSize: 13, fontWeight: 600, color }}>
                {p.estimated > 0 ? `${usagePct}%` : '—'}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <UsageBar pct={usagePct} color={color} />
                <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>
                  {t('tok_tasks')} : {p.taskPercent}%
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
